import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ToastService, ToastType } from '@core/services/toast.service';
import { LucideDynamicIcon } from '@lucide/angular';
import { AlertCircle, CheckCircle, Info, X, XCircle } from '@core/icons/lucide.icons';

@Component({
  selector: 'app-toast-container',
  imports: [LucideDynamicIcon],
  template: `
    <div class="fixed right-4 bottom-4 z-50 flex max-w-md flex-col gap-2">
      @for (toast of toastService.activeToasts(); track toast.id) {
        <div
          class="animate-slide-in flex items-start gap-3 rounded-lg border p-4 shadow-lg transition-all duration-300"
          [class]="getToastClasses(toast.type)"
          role="alert"
        >
          <!-- Icon -->
          <div class="shrink-0">
            @switch (toast.type) {
              @case ('success') {
                <svg [lucideIcon]="CheckCircleIcon" class="h-5 w-5 text-green-600" />
              }
              @case ('error') {
                <svg [lucideIcon]="XCircleIcon" class="h-5 w-5 text-red-600" />
              }
              @case ('warning') {
                <svg [lucideIcon]="AlertCircleIcon" class="h-5 w-5 text-yellow-600" />
              }
              @default {
                <svg [lucideIcon]="InfoIcon" class="h-5 w-5 text-blue-600" />
              }
            }
          </div>

          <!-- Message -->
          <p class="flex-1 text-sm font-medium text-gray-900">{{ toast.message }}</p>

          <!-- Close Button -->
          <button
            type="button"
            (click)="toastService.dismiss(toast.id)"
            class="shrink-0 rounded-full p-1 transition-colors hover:bg-gray-200"
            aria-label="Dismiss"
          >
            <svg [lucideIcon]="XIcon" class="h-4 w-4 text-gray-500" />
          </button>
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      @keyframes slide-in {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      .animate-slide-in {
        animation: slide-in 0.3s ease-out;
      }
    `,
  ],
})
export class ToastContainerComponent {
  readonly toastService = inject(ToastService);

  // Icons
  CheckCircleIcon = CheckCircle;
  XCircleIcon = XCircle;
  AlertCircleIcon = AlertCircle;
  InfoIcon = Info;
  XIcon = X;

  getToastClasses(type: ToastType): string {
    const base = 'bg-white';
    switch (type) {
      case 'success':
        return `${base} border-green-200`;
      case 'error':
        return `${base} border-red-200`;
      case 'warning':
        return `${base} border-yellow-200`;
      case 'info':
        return `${base} border-blue-200`;
      default:
        return `${base} border-gray-200`;
    }
  }
}
